import React, { createContext, useState } from 'react';
import type { AuthProviderInterface, AuthProviderValue, User } from './types';

const defaultUser = {
  loggedIn: false,
  isTwoFactorEnabled: false,
  isTwoFactorAuthenticated: false,
  hasStrongAuthentication: false,
  impersonatorUserToken: '',
  impersonatorEmail: '',
  isUserBlocked: false,
  hasAccess: false,
  roles: [],
};

export const AuthContext = createContext<AuthProviderValue>({
  user: {
    ...defaultUser,
  },
  sessionExpirationDate: 0,
  signin: () => undefined,
  signout: () => undefined,
  refreshSessionExpirationDate: () => undefined,
});

const useAuthProvider = ({ initialUser, initialSessionExpirationDate }: AuthProviderInterface): AuthProviderValue => {
  const [user, setUser] = useState({
    ...defaultUser,
    ...initialUser,
  });
  const [sessionExpirationDate, setSessionExpirationDate] = useState(initialSessionExpirationDate);

  const signin = (user: User) => {
    setUser(user);
  };

  const signout = (): void => {
    setUser({
      ...defaultUser,
    });
  };

  const refreshSessionExpirationDate = (remainingMs: number): void => {
    // api returns session seconds remaining, add to current time
    setSessionExpirationDate(Date.now() + remainingMs);
  };

  return {
    user,
    signin,
    signout,
    sessionExpirationDate,
    refreshSessionExpirationDate,
  };
};

export const createAuthProvider = ({
  initialUser,
  initialSessionExpirationDate,
}: AuthProviderInterface): (({ children }: { children: React.ReactElement }) => JSX.Element) => {
  // eslint-disable-next-line react/prop-types,react/display-name
  return ({ children }) => {
    const auth = useAuthProvider({ initialUser, initialSessionExpirationDate });

    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
  };
};
